import { FunctionComponent, useState, useEffect } from "react";

interface MigrationCalculatorProps {
    onClose: () => void;
    prefill?: {
        clients?: number;
        caregivers?: number;
        employees?: number;
        agencyName?: string;
    };
}

interface MemberAllocation {
    index: number;
    clients: number;
    caregivers: number;
    employees: number;
    unitsPerDay: number;
}

// Constants
const UNITS_PER_MEMBER_PER_DAY = 275;
const CLIENT_UNITS = 20;
const CAREGIVER_UNITS = 15;
const EMPLOYEE_UNITS = 10;
const DEFAULT_MAX_DAYS = 4;

/** Distribute `total` items across `n` members as evenly as possible (integer, remainder on first members) */
function distribute(total: number, n: number): number[] {
    if (n <= 0) return [];
    const base = Math.floor(total / n);
    const remainder = total % n;
    return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
}

const MigrationCalculator: FunctionComponent<MigrationCalculatorProps> = ({ onClose, prefill }) => {
    const [clients, setClients] = useState<number>(prefill?.clients ?? 0);
    const [caregivers, setCaregivers] = useState<number>(prefill?.caregivers ?? 0);
    const [employees, setEmployees] = useState<number>(prefill?.employees ?? 0);
    const [days, setDays] = useState<number>(DEFAULT_MAX_DAYS);
    const [manualMembers, setManualMembers] = useState<number | null>(null); // null = auto
    const [showDetail, setShowDetail] = useState(false);

    useEffect(() => {
        if (prefill) {
            setClients(prefill.clients ?? 0);
            setCaregivers(prefill.caregivers ?? 0);
            setEmployees(prefill.employees ?? 0);
            setManualMembers(null);
        }
    }, [prefill]);

    const totalUnits = clients * CLIENT_UNITS + caregivers * CAREGIVER_UNITS + employees * EMPLOYEE_UNITS;

    // If members is manually set, derive days. Otherwise derive members from days.
    const unitsPerWindow = UNITS_PER_MEMBER_PER_DAY * days;
    const autoMembers = unitsPerWindow > 0 ? Math.ceil(totalUnits / unitsPerWindow) : 0;
    const membersNeeded = manualMembers !== null ? manualMembers : autoMembers;
    const derivedDays = (manualMembers !== null && manualMembers > 0 && totalUnits > 0)
        ? Math.ceil(totalUnits / (manualMembers * UNITS_PER_MEMBER_PER_DAY))
        : days;
    const effectiveDays = manualMembers !== null ? derivedDays : days;

    const totalCapacity = membersNeeded * UNITS_PER_MEMBER_PER_DAY * effectiveDays;
    const utilizationPct = totalCapacity > 0 ? Math.round((totalUnits / totalCapacity) * 100) : 0;

    const handleSetDays = (d: number) => {
        setDays(d);
        setManualMembers(null); // switching to days mode clears manual members
    };

    const handleSetMembers = (m: number) => {
        setManualMembers(m > 0 ? m : 1);
        // sync the days slider to the derived value so it doesn't jump when switching back
        if (m > 0 && totalUnits > 0) {
            setDays(Math.ceil(totalUnits / (m * UNITS_PER_MEMBER_PER_DAY)));
        }
    };

    // Build per-member allocations (totals over the full window)
    const memberAllocations: MemberAllocation[] = (() => {
        if (membersNeeded === 0) return [];
        const clientDist = distribute(clients, membersNeeded);
        const caregiverDist = distribute(caregivers, membersNeeded);
        const employeeDist = distribute(employees, membersNeeded);
        return Array.from({ length: membersNeeded }, (_, i) => {
            const c = clientDist[i];
            const cg = caregiverDist[i];
            const em = employeeDist[i];
            const cPerDay = effectiveDays > 0 ? Math.ceil(c / effectiveDays) : 0;
            const cgPerDay = effectiveDays > 0 ? Math.ceil(cg / effectiveDays) : 0;
            const emPerDay = effectiveDays > 0 ? Math.ceil(em / effectiveDays) : 0;
            return {
                index: i + 1,
                clients: c,
                caregivers: cg,
                employees: em,
                unitsPerDay: cPerDay * CLIENT_UNITS + cgPerDay * CAREGIVER_UNITS + emPerDay * EMPLOYEE_UNITS,
            };
        });
    })();

    const handleInput = (setter: (v: number) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseInt(e.target.value);
        setter(isNaN(val) || val < 0 ? 0 : val);
    };

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 z-[111111]">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 11h.01M12 11h.01M15 11h.01M4 19h16a2 2 0 002-2V7a2 2 0 00-2-2H4a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-white m-0">Migration Calculator</h3>
                            {prefill?.agencyName && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 m-0">{prefill.agencyName}</p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 cta-red transition mt-0.5 ml-4 flex-shrink-0 pt-1"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-6">
                    {/* Inputs */}
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-2 gap-4">
                            {/* Days control */}
                            <div className={`rounded-xl p-4 border-2 transition-colors migration-card-blg ${manualMembers === null ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                        Days
                                    </label>
                                    {manualMembers !== null && (
                                        <span className="text-xs text-blue-500 font-medium">
                                            = {derivedDays}d calculated
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="range" min={1} max={14}
                                        value={manualMembers !== null ? Math.min(derivedDays, 14) : days}
                                        onChange={e => handleSetDays(Number(e.target.value))}
                                        className="flex-1 accent-blue-600"
                                    />
                                    <span className="w-14 text-center migration-card-blg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white font-bold text-sm py-1 px-2 rounded-lg">
                                        {manualMembers !== null ? derivedDays : days}d
                                    </span>
                                </div>
                                <p className="text-xs text-gray-400 m-0 mt-1">
                                    {manualMembers === null ? "Drag to set days → members auto-calculates" : "Set by member count"}
                                </p>
                            </div>

                            {/* Members control */}
                            <div className={`rounded-xl p-4 border-2 transition-colors migration-card-blg ${manualMembers !== null ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20" : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                                        Members
                                    </label>
                                    {manualMembers === null && (
                                        <span className="text-xs text-blue-500 font-medium">
                                            = {autoMembers} calculated
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <input
                                        type="range" min={1} max={20}
                                        value={membersNeeded || 1}
                                        onChange={e => handleSetMembers(Number(e.target.value))}
                                        className="flex-1 accent-blue-600"
                                    />
                                    <span className="w-14 migration-card-blg text-center bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white font-bold text-sm py-1 px-2 rounded-lg">
                                        {membersNeeded}m
                                    </span>
                                </div>
                                <p className="text-xs text-gray-400 m-0 mt-1">
                                    {manualMembers !== null ? "Drag to set members → days auto-calculates" : "Set by day count"}
                                </p>
                            </div>
                        </div>

                        {/* Entity inputs — 3 columns */}
                        <div className="grid grid-cols-3 gap-4 min-w-0">
                            {[
                                { label: "Clients", value: clients, setter: setClients, unit: CLIENT_UNITS },
                                { label: "Caregivers", value: caregivers, setter: setCaregivers, unit: CAREGIVER_UNITS },
                                { label: "Employees", value: employees, setter: setEmployees, unit: EMPLOYEE_UNITS },
                            ].map(({ label, value, setter, unit }) => (
                                <div key={label} className=" rounded-xl  min-w-0">
                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                                        {label}
                                    </label>
                                    <input
                                        type="number" min={0} value={value}
                                        onChange={handleInput(setter)}
                                        className="self-stretch border-[1px] border-solid border-surface-border migration-input-dv  dark:bg-slate-700 rounded-radius-md bg-text-text-tertiary overflow-hidden h-[20px] flex flex-row items-center justify-start py-2.5 px-[15px] gap-[8px] border-[1px] border-solid-dv border-surface-border mq450:flex-wrap
           text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                    <p className="text-xs text-gray-400 mt-1.5 m-0">
                                        {unit} units · {value * unit} total
                                    </p>
                                </div>
                            ))}

                        </div>

                        {/* Total units — full width */}
                        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-between">
                            <div>
                                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide m-0 mb-1">Total Units</p>
                                <p className="text-xs text-gray-400 m-0">
                                    {clients}×{CLIENT_UNITS} + {caregivers}×{CAREGIVER_UNITS} + {employees}×{EMPLOYEE_UNITS}
                                </p>
                            </div>
                            <p className="text-3xl font-bold text-gray-900 dark:text-white m-0">{totalUnits.toLocaleString()}</p>
                        </div>
                    </div>

                    {/* Result */}
                    <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-6">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest m-0 mb-4">Members Needed</p>
                        <div className="flex items-center gap-4 mb-5">
                            <div className="w-16 h-16 rounded-2xl bg-teal-600 flex items-center justify-center shrink-0">
                                <span className="text-2xl font-bold text-white">{membersNeeded}</span>
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                                    {membersNeeded} member{membersNeeded !== 1 ? "s" : ""}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 m-0">
                                    over {effectiveDays} day{effectiveDays !== 1 ? "s" : ""}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white dark:bg-gray-700 border border-gray-200 border-[1px] border-solid border-surface-border dark:border-gray-600 rounded-xl p-3 text-center border-solid-dv">
                                <p className="text-xl font-bold text-gray-900 dark:text-white m-0">{effectiveDays}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 m-0 mt-0.5">Days</p>
                            </div>
                            <div className="bg-white dark:bg-gray-700 border border-gray-200 border-[1px] border-solid border-surface-border dark:border-gray-600 rounded-xl p-3 text-center border-solid-dv">
                                <p className="text-xl font-bold text-gray-900 dark:text-white m-0">{UNITS_PER_MEMBER_PER_DAY}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 m-0 mt-0.5">Units/member/day</p>
                            </div>
                            <div className="bg-white dark:bg-gray-700 border border-gray-200 border-[1px] border-solid border-surface-border dark:border-gray-600 rounded-xl p-3 text-center border-solid-dv">
                                <p className={`text-xl font-bold m-0 ${utilizationPct > 90 ? "text-orange-500" : "text-teal-600"}`}>{utilizationPct}%</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 m-0 mt-0.5">Utilization</p>
                            </div>
                        </div>
                    </div>

                    {/* Per-member detail (expandable) */}
                    {membersNeeded > 0 && (
                        <div className="border border-gray-200 dark:border-gray-700 overflow-hidden">
                            {/* Accordion header */}
                            <button
                                className="w-full flex items-center justify-between calculate-btn-items px-5 py-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                                onClick={() => setShowDetail(v => !v)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-7 h-7 rounded-lg bg-teal-50 dark:bg-teal-900/30 flex items-center justify-center">
                                        <svg className="w-4 h-4 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </div>
                                    <div className="text-left">
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white m-0">Member Breakdown</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 m-0">
                                            {membersNeeded} member{membersNeeded !== 1 ? "s" : ""} · {effectiveDays} day{effectiveDays !== 1 ? "s" : ""}
                                        </p>
                                    </div>
                                </div>
                                <svg
                                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showDetail ? "rotate-180" : ""}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {showDetail && (
                                <div className="border-t border-gray-100 dark:border-gray-700 mt-4">
                                    {/* Column headers */}
                                    <div className="grid grid-cols-5 px-5 py-2.5 bg-gray-50 dark:bg-gray-800/60">
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Member</span>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center">Clients</span>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center">Caregivers</span>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-center">Employees</span>
                                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide text-right">Units/day</span>
                                    </div>

                                    <div className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                        {memberAllocations.map(m => {
                                            const loadPct = UNITS_PER_MEMBER_PER_DAY > 0
                                                ? Math.round((m.unitsPerDay / UNITS_PER_MEMBER_PER_DAY) * 100)
                                                : 0;
                                            return (
                                                <div key={m.index} className="grid grid-cols-5 items-center px-5 py-3 hover:bg-gray-50/60 dark:hover:bg-gray-800/40 transition-colors">
                                                    {/* Member avatar */}
                                                    <div className="flex items-center gap-2.5">
                                                        <div className="w-7 h-7 rounded-full bg-teal-600 flex items-center justify-center shrink-0">
                                                            <span className="text-xs font-bold text-white">{m.index}</span>
                                                        </div>
                                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">M{m.index}</span>
                                                    </div>
                                                    {/* Clients */}
                                                    <div className="text-center">
                                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{m.clients}</span>
                                                        <p className="text-xs text-gray-400 m-0">{effectiveDays > 0 ? Math.ceil(m.clients / effectiveDays) : 0}/day</p>
                                                    </div>
                                                    {/* Caregivers */}
                                                    <div className="text-center">
                                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{m.caregivers}</span>
                                                        <p className="text-xs text-gray-400 m-0">{effectiveDays > 0 ? Math.ceil(m.caregivers / effectiveDays) : 0}/day</p>
                                                    </div>
                                                    {/* Employees */}
                                                    <div className="text-center">
                                                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{m.employees}</span>
                                                        <p className="text-xs text-gray-400 m-0">{effectiveDays > 0 ? Math.ceil(m.employees / effectiveDays) : 0}/day</p>
                                                    </div>
                                                    {/* Units/day with mini bar */}
                                                    <div className="text-right">
                                                        <span className={`text-sm font-semibold ${loadPct > 90 ? "text-orange-500" : "text-teal-600 dark:text-teal-400"}`}>
                                                            {m.unitsPerDay}
                                                        </span>
                                                        <div className="mt-1 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full ${loadPct > 90 ? "bg-orange-400" : "bg-teal-500"}`}
                                                                style={{ width: `${Math.min(loadPct, 100)}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Totals row */}
                                        <div className="grid grid-cols-5 items-center px-5 py-3 bg-gray-50 dark:bg-gray-800/60">
                                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total</span>
                                            <span className="text-sm font-bold text-gray-900 dark:text-white text-center">{clients}</span>
                                            <span className="text-sm font-bold text-gray-900 dark:text-white text-center">{caregivers}</span>
                                            <span className="text-sm font-bold text-gray-900 dark:text-white text-center">{employees}</span>
                                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 text-right">{totalUnits.toLocaleString()} units</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Rate reference */}
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                        <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide m-0 mb-2">Rate Reference</p>
                        <div className="grid grid-cols-3 gap-2 text-xs text-amber-800 dark:text-amber-300">
                            <span>1 Client = {CLIENT_UNITS} units</span>
                            <span>1 Caregiver = {CAREGIVER_UNITS} units</span>
                            <span>1 Employee = {EMPLOYEE_UNITS} units</span>
                        </div>
                        <p className="text-xs text-amber-700 dark:text-amber-400 m-0 mt-2">
                            Each member handles {UNITS_PER_MEMBER_PER_DAY} units/day over {effectiveDays} day{effectiveDays !== 1 ? "s" : ""}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MigrationCalculator;
